/**
 * Buildings-specific preferences hook.
 *
 * Wraps useListingPreferences with:
 *  - The buildings legacy migrator (converts old Kendo cache shapes).
 *  - Column policy resolved from the current scope.
 *  - The scope-appropriate localStorage cache key.
 *  - Default buildings filter values.
 *
 * Returns the same shape as useListingPreferences so the parent component
 * can use it directly to initialize useListingQueryState.
 *
 * IP-15282: memoize all computed inputs to ensure savePreferences and policy
 * do not change identity on every render.
 */
import { useMemo } from 'react';
import { useListingPreferences } from '@hooks/listings';
import { INITIAL_BUILDING_FILTERS } from '../types';
import { columnPolicyForScope, cacheKeyForScope } from '../adapters/buildingsColumnPolicy';
import { createBuildingsLegacyMigrator } from '../adapters/buildingsLegacyMigrator';
const DEFAULT_PAGE_SIZE = 25;
/**
 * Loads, migrates, and saves Buildings listing preferences.
 *
 * The returned `preferences` object (when non-null) provides:
 *  - pageSize, sort, filters, activeFilterKeys, visibleColumnKeys
 *
 * When `preferences` is null, the parent should use feature defaults
 * (INITIAL_BUILDING_FILTERS, DEFAULT_ACTIVE_FILTER_KEYS, default sort).
 */
export function useBuildingsPreferences(options) {
    const { scope } = options;
    // IP-15282: memoize all derived objects so they are stable across renders.
    const cacheKey = useMemo(() => cacheKeyForScope(scope), [scope]);
    const policy = useMemo(() => columnPolicyForScope(scope), [scope]);
    const legacyMigrator = useMemo(() => createBuildingsLegacyMigrator(scope), [scope]);
    return useListingPreferences({
        cacheKey,
        policy,
        defaultFilters: INITIAL_BUILDING_FILTERS,
        defaultPageSize: DEFAULT_PAGE_SIZE,
        legacyMigrator
    });
}
